import React, { useEffect, useState } from 'react';
import useSelfCelebration from '../hooks/useSelfCelebration';
import { useApp } from '../context/AppContext';
import { todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';

const STORAGE_KEY = 'trackwithtux_celebration_seen';

// A bigger, more celebratory popup than the top announcement bar — shown to
// the person themselves, once, the first time they open the app on their
// actual birthday or work-anniversary day. Persisted in localStorage (not
// the banner's session-only flag) since "once on first open" needs to
// survive a page reload later the same day, but the date-scoped key still
// resets it naturally the next day without any cleanup needed.
export default function CelebrationPopup() {
  const { currentUser } = useApp();
  const celebration = useSelfCelebration();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!celebration || !currentUser) return;
    const today = todayDateStringInTz(DAY_ANCHOR_TZ);
    let seen = null;
    try {
      seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      seen = null;
    }
    const alreadySeenToday = seen && seen.uuid === currentUser.uuid && seen.date === today;
    if (!alreadySeenToday) setOpen(true);
    // Only ever needs to check once, right when we learn it's someone's day
    // — not on every currentUser/celebration reference change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebration && celebration.type, currentUser && currentUser.uuid]);

  const dismiss = () => {
    if (currentUser) {
      const today = todayDateStringInTz(DAY_ANCHOR_TZ);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ uuid: currentUser.uuid, date: today }));
      } catch (e) {
        // Private browsing / storage disabled — worst case it shows again
        // next reload today, not worth failing the dismiss over.
      }
    }
    setOpen(false);
  };

  if (!open || !celebration || !currentUser) return null;

  const firstName = currentUser.name.split(' ')[0];
  const isBirthday = celebration.type === 'birthday';
  const yearsLabel = !isBirthday ? `${celebration.years} year${celebration.years === 1 ? '' : 's'}` : '';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/40 backdrop-blur-sm px-4">
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-lg p-6 sm:p-8 text-center">
        <div className="text-5xl mb-3">{isBirthday ? '🎂' : '🏆'}</div>
        <h2 className="text-xl font-bold text-ink">
          {isBirthday ? `Happy Birthday, ${firstName}! 🎉` : `Happy ${yearsLabel} Anniversary, ${firstName}! 🎊`}
        </h2>
        <p className="mt-2 text-sm text-muted">
          {isBirthday
            ? 'Wishing you an amazing day — thank you for being part of the team!'
            : `Thank you for ${yearsLabel} with TUX Leads — here's to more!`}
        </p>
        <button
          onClick={dismiss}
          className="mt-6 w-full py-2.5 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors"
        >
          Thanks! 🎉
        </button>
      </div>
    </div>
  );
}
